import { useState } from 'react';
import { maskSessionToken } from './settingsDisplay';

type SessionTokenFieldProps = {
  token?: string | null;
  persist: boolean;
  onPersistChange: (on: boolean) => void;
  revealed?: boolean;
  onRevealChange?: (on: boolean) => void;
};

export function SessionTokenField({
  token,
  persist,
  onPersistChange,
  revealed,
  onRevealChange,
}: SessionTokenFieldProps) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [internalReveal, setInternalReveal] = useState(false);
  const isRevealed = onRevealChange ? !!revealed : internalReveal;
  const setReveal = onRevealChange ?? setInternalReveal;
  const masked = maskSessionToken(token);
  const hasToken = Boolean(token);

  async function copyToken() {
    if (!token) return;
    setCopyFailed(false);
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
    } catch {
      setCopied(false);
      setCopyFailed(true);
    }
  }

  return (
    <div>
      <div className="text-xs text-gray-500">Token</div>
      <div className="flex items-center gap-2 flex-wrap">
        <code data-testid="session-token-mask" className="text-xs">{masked}</code>
        {isRevealed && token ? (
          <input
            data-testid="session-token-reveal"
            type="text"
            readOnly
            value={token}
            className="border rounded p-1 text-xs font-mono max-w-full"
            aria-label="Session token (revealed)"
          />
        ) : null}
        <button
          type="button"
          className="text-xs border rounded px-2 py-1"
          onClick={copyToken}
          disabled={!hasToken}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button
          type="button"
          className="text-xs border rounded px-2 py-1"
          onClick={() => setReveal(!isRevealed)}
          disabled={!hasToken}
          aria-pressed={isRevealed}
        >
          {isRevealed ? 'Hide' : 'Reveal'}
        </button>
        <label className="text-xs flex items-center gap-1">
          <input
            type="checkbox"
            checked={persist}
            onChange={(e) => onPersistChange(e.target.checked)}
          />
          Persist token (localStorage)
        </label>
      </div>
      {copyFailed ? (
        <div className="text-xs text-gray-500 mt-1">Copy failed — use Reveal if you must read the token.</div>
      ) : null}
    </div>
  );
}
